import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from './auth-store.js';

import './auth.css';

const LAST_WORKSPACE_KEY = 'inbx.last_workspace_slug';
const LAST_EMAIL_KEY = 'inbx.last_email';

/** Безопасное чтение/запись (localStorage недоступен в приватном режиме Safari / при quota). */
function readLs(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}
/** Куда вернуться после входа — только адрес этого же сайта, без чужих доменов. */
function safeRedirect(raw: string | null): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/login')) return null;
  return raw;
}

function writeLs(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const error = useAuthStore((s) => s.error);
  const loading = useAuthStore((s) => s.loading);

  // Пришли по ссылке (задали пароль, пространство заведено из кабинета) —
  // пространство и почта уже подставлены.
  const query = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState(() => query.get('email') ?? readLs(LAST_EMAIL_KEY));
  const [password, setPassword] = useState('');
  const [workspaceSlug, setWorkspaceSlug] = useState(
    () => query.get('workspace') ?? readLs(LAST_WORKSPACE_KEY),
  );
  const [totp, setTotp] = useState('');
  const [twoFaRequired, setTwoFaRequired] = useState(false);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    try {
      await login(email, password, workspaceSlug);
      const back = safeRedirect(query.get('redirect'));
      if (back) window.location.assign(back);
      else navigate({ to: '/inbox' });
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth.2fa_required' || code === 'auth.2fa_invalid') {
        setTwoFaRequired(true);
      }
    }
  }

  return (
    <div className="inbx-auth-page">
      <form className="inbx-auth-form" onSubmit={onSubmit}>
        <h1 className="inbx-auth-title">INBX</h1>
        <label>
          Workspace
          <input
            type="text"
            name="workspace"
            id="workspace"
            value={workspaceSlug}
            onChange={(e) => {
              setWorkspaceSlug(e.target.value);
              writeLs(LAST_WORKSPACE_KEY, e.target.value);
            }}
            placeholder="my-company"
            required
            autoComplete="on"
          />
        </label>
        <label>
          {t('auth.email')}
          <input
            type="email"
            name="email"
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              writeLs(LAST_EMAIL_KEY, e.target.value);
            }}
            required
            autoComplete="username"
          />
        </label>
        <label>
          {t('auth.password')}
          <input
            type="password"
            name="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>
        {twoFaRequired && (
          <label>
            {t('auth.2fa.code')}
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              autoComplete="one-time-code"
              required
            />
          </label>
        )}
        {error && <div className="inbx-auth-error">{error}</div>}
        <button type="submit" disabled={loading}>
          {loading ? t('common.loading') : t('auth.login')}
        </button>
        <a href="/forgot-password" className="inbx-auth-link">
          {t('auth.forgot_password')}
        </a>
      </form>
    </div>
  );
}
